/**
 * UnpackClasses takes a string of space-separated classes and returns an array of those classes.
 * @param classes - The classes to unpack.
 * @returns the array of classes.
 */
export function unpackClasses(classes) {
    return classes.split(" ");
}

/**
 * It takes a list of classes, filters out the falsy ones, and joins the rest with a space
 * @param classes - The classes to pack.
 * @returns A function that takes in a variable number of arguments and returns a string of all the
 * arguments that are truthy.
 */
export function packClasses(...classes) {
    return classes.filter(Boolean).join(" ");
}

/**
 * It takes a string of space-separated class names, and replaces the first occurrence of `oldClass`
 * with `newClass`
 * @param classNames - The class names to be unpacked.
 * @param oldClass - The class you want to replace
 * @param newClass - The class to add to the classNames
 * @returns A function that takes a classNames string and returns a new string with the oldClass
 * replaced with the newClass.
 */
export function replaceClass(classNames, oldClass, newClass) {
    const classes = unpackClasses(classNames);
    if (classes.indexOf(oldClass) === -1) {
        return [...classes, newClass].join(" ");
    }

    return [...classes.filter((c) => c !== oldClass), newClass].join(" ");
}

/**
 * Given a string of space-separated class names, return true if the class name is present in the
 * string.
 * @param classNames - a string of space-separated class names
 * @param checkClass - The class name to check for
 * @returns A function that takes in a classNames and checkClass and returns a boolean.
 */
export function hasClass(classNames, checkClass) {
    if (!classNames || !classNames.length) {
        return false;
    }
    const classes = unpackClasses(classNames);
    return classes.indexOf(checkClass) !== -1;
}

/**
 * Return a random element from an array.
 * @param arr - The array to get a random element from.
 * @returns a random element from the array.
 */
export function arrayRandom(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * It returns a random string of characters of a given length
 * NONCES ARE NOT CRYPTOGRAPHICALLY SECURE.
 * You should only use nonces when you simply need a random value
 * for refresh or location purposes.
 * @param [length=16] - The length of the nonce. Defaults to 16.
 * @returns A string of random characters.
 */
export function nonce(length = 16) {
    const out = [];
    const chars =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    for (let i = 0; i < length; i++) {
        out.push(chars.charAt(Math.floor(Math.random() * chars.length)));
    }

    return out.join("");
}

/**
 * Return the nth segment of the current URL path.
 * @param idx - The index of the path segment you want to retrieve.
 * @returns the value of the idx parameter.
 */
export function pathSegment(idx) {
    let parts = window.location.pathname.trim();
    if (parts[0] === "/") {
        parts = parts.substr(1);
    }
    if (parts[parts.length - 1] === "/") {
        parts = parts.substr(-1);
    }
    parts = parts.split("/");
    if (typeof parts[idx] !== "undefined") {
        return parts[idx];
    }

    return null;
}

/**
 * It takes a key and returns the value of the query string parameter with that key
 * @param key - The key to search for in the query string.
 * @returns The value of the key in the query string.
 */
export function pathQuery(key) {
    let parts = window.location.search;
    if (!parts) {
        return null;
    }
    parts = parts.substr(1);
    parts = parts.split("&");
    for (let i = 0; i < parts.length; i++) {
        const values = parts[i].split("=");
        if (values[0] === key) {
            return values[1];
        }
    }
    return null;
}

/**
 * It takes a path, an object of parameters, and a boolean, and returns a path with the parameters
 * appended to it
 * @param path - The path to the route.
 * @param [params] - The parameters to be added to the URL.
 * @param [combine=false] - If true, the params object will be combined with the current query string.
 * @returns A function that takes in a path, params, and combine.
 */
export function buildPath(path, params = {}, combine = false) {
    let useParams = params;
    if (combine === true) {
        useParams = Object.assign(
            {},
            Object.fromEntries(new URLSearchParams(window.location.search)),
            params
        );
    }
    return path.concat("?").concat(new URLSearchParams(useParams));
}

/**
 * It takes a string template and an object of data, and returns a string with the template's
 * placeholders replaced with the data
 * @param template - The template string.
 * @param data - The data object that contains the values to be used in the template.
 * @returns the template with the data replaced.
 */
export function template(template, data) {
    return template.replace(/\{([^}]+)\}/g, (match, key) => {
        return data[key];
    });
}

/**
 * TrimSlashes() removes the leading and trailing slashes from a string.
 * @param str - The string to trim.
 * @returns the string with the first and last slashes removed.
 */
export function trimSlashes(str) {
    return str.replace(/^\/|\/$/g, "");
}

/**
 * If the value is not a number, return null, otherwise return the integer value.
 * @param val - The value to be parsed.
 * @returns the value of the variable 'val' after it has been parsed as an integer.
 */
export function forceInt(val) {
    if (isNaN(parseInt(val))) {
        return null;
    }
    return parseInt(val);
}

/**
 * It takes a string, converts it to lowercase, replaces spaces with dashes, removes non-alphanumeric
 * characters, and removes duplicate dashes
 * @param str - The string to slugify.
 * @returns the string that is passed in, but with all the spaces replaced with dashes.
 */
export function slugify(str) {
    return str
        .toLowerCase()
        .replace(/ {2,}/g, " ")
        .replace(/ /g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/-{2,}/g, "-");
}

/**
 * It takes a URL, a key, and a value, and returns a new URL with the key/value pair added to the query
 * string
 * @param l - The location object.
 * @param key - The key of the parameter to update.
 * @param [val=null] - The value to set the key to. If null, the key will be deleted.
 * @returns A function that takes in a location object and returns a string.
 */
export function updateParams(l, key, val = null) {
    const params = new URLSearchParams(l.search);

    if (typeof key === "object") {
        Object.entries(key).forEach(([k, v]) => {
            if (v === null) {
                params.delete(k);
                return;
            }
            params.set(k, v);
        });
    } else {
        if (params.has(key) && params.get(key) === val) {
            params.delete(key);
        } else {
            params.set(key, val);
        }
    }

    return l.pathname + "?" + params.toString();
}

/**
 * Remove duplicate slashes from a string.
 * @param str - The string to remove duplicate slashes from.
 * @returns a string with all duplicate slashes removed.
 */
export function removeDuplicateSlashes(str) {
    // Safari doesn't support lookbehind...
    // return str.replace(/(?<!:)\/{2,}/g, "/");

    const set = str.match(/([^:]\/{2,3})/g); // Match (NOT ":") followed by (2 OR 3 "/")
    for (let s in set) {
        let replace_with = set[s].substr(0, 1) + "/";
        str = str.replace(set[s], replace_with);
    }

    return str;
}

export function isTrueOrUndefined(thing) {
    return (
        thing === "true" ||
        thing === undefined ||
        thing === true ||
        thing === null
    );
}
